/**
 * Date utility functions for consistent formatting across the application
 */

/**
 * Formats a date string to DD/MM/YYYY format
 * @param dateString - The date string to format
 * @param fallback - Fallback value if date is invalid (default: "")
 * @returns Formatted date string in DD/MM/YYYY format
 */
export const formatDate = (dateString: string | null | undefined, fallback: string = ""): string => {
  if (!dateString) {
    return fallback;
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}`);
      return fallback;
    }

    // Get day, month, year
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() returns 0-11
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error("Error formatting date:", error, "Original value:", dateString);
    return fallback;
  }
};

/**
 * Formats a date string to DD/MM/YYYY HH:MM format
 * @param dateString - The date string to format
 * @param fallback - Fallback value if date is invalid (default: "")
 * @returns Formatted date string in DD/MM/YYYY HH:MM format
 */
export const formatDateTime = (dateString: string | null | undefined, fallback: string = ""): string => {
  if (!dateString) {
    return fallback;
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}`);
      return fallback;
    }

    // Get day, month, year, hours, minutes
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error("Error formatting datetime:", error, "Original value:", dateString);
    return fallback;
  }
};

/**
 * Formats a date string to DD Month YYYY format (e.g., "25 December 2023")
 * @param dateString - The date string to format
 * @param fallback - Fallback value if date is invalid (default: "")
 * @returns Formatted date string in DD Month YYYY format
 */
export const formatDateLong = (dateString: string | null | undefined, fallback: string = ""): string => {
  if (!dateString) {
    return fallback;
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}`);
      return fallback;
    }

    // Get day, month name, year
    const day = date.getDate();
    const month = date.toLocaleString('en', { month: 'long' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    console.error("Error formatting date long:", error, "Original value:", dateString);
    return fallback;
  }
};

/**
 * Checks if a date string is valid
 * @param dateString - The date string to validate
 * @returns True if the date is valid, false otherwise
 */
export const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}; 